import { useCallback, useEffect, useState } from "react";
import { Trash2, UserPlus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { http, errText } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const StaffAccess = ({ open, onOpenChange }) => {
  const [data, setData] = useState({ builtin: [], staff: [] });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get("/admin/staff").then((r) => setData({ builtin: r.data?.builtin ?? [], staff: Array.isArray(r.data?.staff) ? r.data.staff : [] })).catch((e) => toast.error(errText(e))), []);
  useEffect(() => { if (open) load(); return undefined; }, [open, load]);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await http.post("/admin/staff", { email }); setEmail(""); toast.success("Access granted."); load(); }
    catch (x) { toast.error(errText(x)); }
    finally { setBusy(false); }
  };
  const remove = async (em) => {
    if (!window.confirm(`Remove portal access for ${em}?`)) return;
    try { await http.delete(`/admin/staff/${encodeURIComponent(em)}`); toast.success("Access removed."); load(); }
    catch (x) { toast.error(errText(x)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#FBF8F1] rounded-3xl border-2 border-[#154F49]" data-testid="staff-access-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-[#154F49]">Staff access</DialogTitle>
          <DialogDescription className="text-[#465F57]">Google accounts allowed to sign in to this portal. Removed staff lose access on their next action.</DialogDescription>
        </DialogHeader>
        <form onSubmit={add} className="flex gap-2">
          <label htmlFor="staff-email" className="sr-only">Staff Google email</label>
          <input id="staff-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@gmail.com" data-testid="staff-email-input"
            className="flex-1 px-4 py-2.5 rounded-xl border border-[#154F49]/30 bg-white text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CDA34F]" />
          <button type="submit" disabled={busy} className="btn-primary" data-testid="staff-add-btn"><UserPlus className="w-4 h-4" /> Add</button>
        </form>
        <ul className="divide-y divide-[#154F49]/10 border border-[#154F49]/15 rounded-2xl bg-white" data-testid="staff-list">
          {data.builtin.map((em) => (
            <li key={em} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
              <span className="break-all">{em}</span>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-[#9B742A] whitespace-nowrap"><ShieldCheck className="w-3.5 h-3.5" /> Owner</span>
            </li>
          ))}
          {data.staff.map((s) => (
            <li key={s.email} className="flex items-center justify-between gap-3 px-4 py-3 text-sm" data-testid={`staff-row-${s.email}`}>
              <span className="min-w-0"><span className="block break-all">{s.email}</span><span className="text-xs text-[#465F57]">Added by {s.added_by}</span></span>
              <button type="button" onClick={() => remove(s.email)} aria-label={`Remove ${s.email}`} className="p-2 rounded-lg text-red-800 hover:bg-red-50" data-testid={`staff-remove-${s.email}`}><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
        <p className="text-xs text-[#465F57]">Owner accounts are set in the server settings and can't be removed here.</p>
      </DialogContent>
    </Dialog>
  );
};
